/**
 * Profile Page - Microsoft 365 Style
 * User profile display and management
 */

import React, { useEffect, useState } from 'react';
import { makeStyles, Text, Avatar } from '@fluentui/react-components';
import { ContentCard } from '../../components/layout';
import { useAppContext } from '../../contexts/AppContainer';
import { fullDate, VerifyApiClient } from '../../configurations/index.config';
import { VERIFY_API_URL } from '../../configurations/credentials';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
    textAlign: 'center',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  avatarSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '32px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '24px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    fontSize: '12px',
    color: '#605e5c',
    fontWeight: '600',
  },
  value: {
    fontSize: '14px',
    color: '#323130',
  },
});

export const ProfileFluentUI: React.FC = () => {
  const styles = useStyles();
  const { profileData } = useAppContext() as any;
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (profileData?.employeeId) {
      fetchProfileImage(profileData.employeeId);
    }
  }, [profileData?.employeeId]);

  const fetchProfileImage = async (id: string) => {
    try {
      const response = await VerifyApiClient.get(`/profile/get?employee_id=${id}`);
      if (response.data.status === 'success') {
        setImageUrl(response.data.image);
      }
    } catch (error) {
      // Image not found, use default
    }
  };

  const employee = profileData?.employee || {};

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>My Profile</Text>
      </div>

      <ContentCard>
        {/* Avatar Section */}
        <div className={styles.avatarSection}>
          {imageUrl ? (
            <img
              src={`${VERIFY_API_URL}${imageUrl}`}
              style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                objectFit: 'cover',
                marginBottom: '16px',
              }}
              alt="Profile"
            />
          ) : (
            <Avatar
              name={`${employee.first_name || ''} ${employee.last_name || ''}`}
              size={120}
              color="brand"
              style={{ marginBottom: '16px' }}
            />
          )}
          <Text size={600} weight="semibold">
            {employee.first_name} {employee.other_names} {employee.last_name}
          </Text>
          <Text size={300} style={{ color: '#605e5c' }}>
            {employee.unique_emp_id || ''}
          </Text>
        </div>

        {/* Personal Information */}
        <div style={{ marginBottom: '32px' }}>
          <Text size={500} weight="semibold" style={{ display: 'block', marginBottom: '16px' }}>
            Personal Information
          </Text>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <Text className={styles.label}>First Name</Text>
              <Text className={styles.value}>{employee.first_name || '—'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Last Name</Text>
              <Text className={styles.value}>{employee.last_name || '—'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Other Names</Text>
              <Text className={styles.value}>{employee.other_names || '—'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Date of Birth</Text>
              <Text className={styles.value}>
                {employee.date_of_birth ? fullDate(employee.date_of_birth) : '—'}
              </Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Gender</Text>
              <Text className={styles.value}>{employee.gender || '—'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Marital Status</Text>
              <Text className={styles.value}>{employee.marital_status || '—'}</Text>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div style={{ marginBottom: '32px' }}>
          <Text size={500} weight="semibold" style={{ display: 'block', marginBottom: '16px' }}>
            Contact Information
          </Text>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Phone</Text>
              <Text className={styles.value}>{employee.phone || '—'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Email</Text>
              <Text className={styles.value}>{employee.email || '—'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Home Address</Text>
              <Text className={styles.value}>{employee.home_address || '—'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Suburb</Text>
              <Text className={styles.value}>{employee.suburb || '—'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Region</Text>
              <Text className={styles.value}>{employee.region || '—'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Nationality</Text>
              <Text className={styles.value}>{employee.nationality || '—'}</Text>
            </div>
          </div>
        </div>

        {/* Employment Information */}
        <div style={{ marginBottom: '32px' }}>
          <Text size={500} weight="semibold" style={{ display: 'block', marginBottom: '16px' }}>
            Employment Information
          </Text>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Department</Text>
              <Text className={styles.value}>{employee.department_name || '—'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Role</Text>
              <Text className={styles.value}>{employee.role_title || '—'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Hire Date</Text>
              <Text className={styles.value}>
                {employee.hire_date ? fullDate(employee.hire_date) : '—'}
              </Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Tax ID</Text>
              <Text className={styles.value}>{employee.tax_id || '—'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>SSNIT Number</Text>
              <Text className={styles.value}>{employee.ssnit_number || '—'}</Text>
            </div>
          </div>
        </div>

        {/* Banking Information */}
        <div>
          <Text size={500} weight="semibold" style={{ display: 'block', marginBottom: '16px' }}>
            Banking Information
          </Text>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Bank Name</Text>
              <Text className={styles.value}>{employee.bank_name || '—'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Branch</Text>
              <Text className={styles.value}>{employee.branch || '—'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Account Name</Text>
              <Text className={styles.value}>{employee.account_name || '—'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.label}>Account Number</Text>
              <Text className={styles.value}>{employee.account_number || '—'}</Text>
            </div>
          </div>
        </div>
      </ContentCard>
    </div>
  );
};

export default ProfileFluentUI;
